// Textos de la interfaz para los valores del dominio (español de México, de tú).
import { ETIQUETA_ESTADO, numero, pesos } from '@cotizador/compartido';
import type { EstadoCotizacion, FormatoFamilia, OpcionId, Rol, Semaforo, TipoProyecto, VeredictoRevision } from '@cotizador/compartido';

export { ETIQUETA_ESTADO };

export const ETIQUETA_TIPO: Readonly<Record<TipoProyecto, string>> = {
  casa: 'Casa',
  comercio: 'Comercio',
  industria: 'Industria',
  baterias: 'Baterías / híbrido',
  aislado: 'Aislado (sin CFE)',
  ampliacion_baterias: 'Ampliación de baterías',
};

/** El formato del PDF para leerlo (el id interno «baterias» va sin acento). */
export const ETIQUETA_FORMATO: Readonly<Record<FormatoFamilia, string>> = {
  residencial: 'residencial',
  comercial: 'comercial',
  industrial: 'industrial',
  baterias: 'baterías',
  horaria: 'horaria',
  desglose: 'desglose',
  piezas: 'piezas',
  multisitio: 'multisitio',
  // ADENDA z68-3: el todo en uno con casas y bimestrales
  consolidado: 'todo en uno',
};

/** El tipo y, en baterías, aislado o ampliación de baterías, para quién es (cliente.tipo, elegido en «Qué cotizas»): «Baterías / híbrido · Industria».
 *  Antes no se guardaba y en la cotización no se veía si las baterías eran de una casa o de una planta. */
export function etiquetaTipoDe(q: { tipo: TipoProyecto; cliente: { tipo?: 'casa' | 'comercio' | 'industria' | null | undefined } }): string {
  return ETIQUETA_TIPO[q.tipo] + ((q.tipo === 'baterias' || q.tipo === 'aislado' || q.tipo === 'ampliacion_baterias') && q.cliente.tipo ? ` · ${ETIQUETA_TIPO[q.cliente.tipo]}` : '');
}

export const DESCRIPCION_TIPO: Readonly<Record<TipoProyecto, string>> = {
  casa: 'Tarifa doméstica (1 a 1F, DAC).',
  comercio: 'Negocio en baja tensión: PDBT, GDBT.',
  industria: 'Media tensión (GDMTO, GDMTH) o planta.',
  baterias: 'Respaldo o híbrido con baterías.',
  aislado: 'Sin red de CFE: rancho, bomba, cabaña.',
  ampliacion_baterias: 'Sumar baterías a un sistema que ya tiene.',
};

export const ETIQUETA_OPCION: Readonly<Record<OpcionId, string>> = { arranque: 'Arranque', exacta: 'Exacta', holgada: 'Holgada' };

export const ETIQUETA_ROL: Readonly<Record<Rol, string>> = {
  director: 'Director',
  asesor: 'Asesor',
  externo: 'Vendedor externo',
  tecnico: 'Técnico',
};

export const ETIQUETA_SEMAFORO: Readonly<Record<Semaforo, string>> = { verde: 'Rentable', amarillo: 'Margen justo', rojo: 'No conviene' };

/** Color del semáforo de margen de una opción, nivel o registro del CRM, tal como lo mandó el servidor (Diego, 11-oct):
 *  el director lo trae dentro de la rentabilidad completa; un asesor interno, SOLO el color en `semaforo_margen`; un
 *  externo, nada (null). La interfaz nunca lo calcula. */
export function colorSemaforo(x: { rentabilidad?: { semaforo: Semaforo } | null; semaforo_margen?: Semaforo | null } | null | undefined): Semaforo | null {
  return x?.rentabilidad?.semaforo ?? x?.semaforo_margen ?? null;
}

export const ETIQUETA_VEREDICTO: Readonly<Record<VeredictoRevision, string>> = {
  bien: 'Hace sentido',
  revisar: 'Revísala',
  pierde_dinero: 'Pierde dinero',
};

export const ORDEN_ESTADOS: readonly EstadoCotizacion[] = ['borrador', 'enviada', 'seguimiento', 'visita_agendada', 'ganada', 'perdida', 'cancelada'];

export function dinero(n: number | null | undefined, enteros = true): string {
  if (n === null || n === undefined) return '—';
  return pesos(n, { enteros });
}

export function kwp(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${numero(n, n < 100 ? 2 : 1)} kWp`;
}

export function kwh(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${numero(n)} kWh`;
}

export function porcentaje(fraccion: number, decimales = 0): string {
  return `${numero(fraccion * 100, decimales)} %`;
}

export function plural(n: number, uno: string, varios: string): string {
  return `${numero(n)} ${n === 1 ? uno : varios}`;
}

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${numero(n / 1024)} KB`;
  return `${numero(n / 1024 / 1024, 1)} MB`;
}

/** El ahorro al mes de la tarjeta: el mismo que imprime el PDF (compartido: ahorroMensualImpreso). */
export { ahorroMensualImpreso, ahorroPorPeriodoImpreso } from '@cotizador/compartido';

/** La tasa de ISR como la imprime la propuesta (textos-comunes.mjs: String(tasa)): 30, 35.5. Verificación adversarial de la
 *  etapa 2: la tarjeta redondeaba a entero ("ISR 36 %") y el PDF decía "ISR de 35.5 %". */
export function tasaIsr(t: number): string {
  return String(t);
}

